import { useLayoutEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import Button from '@mui/material/Button';
import LinkIcon from '@mui/icons-material/Link';
import { FormattedMessage } from 'react-intl';
import DownloadIcon from '@/assets/download.svg?react';
import { useDownload } from '@/hooks/useDownload';
import { useBasketContext } from '@/contexts/BasketContext';
import useHasUserPermissions from '@/hooks/useHasUserPermissions';
import { SHARE_BASKET_PERMISSIONS } from '@/constants/Permissions';
import { SharedBasketItemCategory } from '@/lib/types';
import {
  useReportBottomOverlay,
  useSideClearance,
} from '@/lib/bottomOverlays';

export type BasketBarVariant = 'sticky' | 'fixed' | 'footer';

// Hidden for now while the select-all-loaded behaviour is being reconsidered;
// the hosts still pass `category`/`loadedIds` so flipping this back restores it.
const SHOW_SELECT_ALL_LOADED = false;

// Minimum gutter either side of the `fixed` bar, widened by whatever is
// docked in that corner.
const FIXED_GUTTER = 40;

// The bar stops yielding once it is down to this, so a narrow viewport with
// both managers open leaves something readable rather than a sliver.
const MIN_BAR_WIDTH = 320;

const CategoryLabels: Record<
  SharedBasketItemCategory,
  { id: string; defaultMessage: string }
> = {
  compliance_documents: {
    id: 'basketBreakdownCompliance',
    defaultMessage: '{count} compliance',
  },
  marketing_assets: {
    id: 'basketBreakdownMarketing',
    defaultMessage: '{count} marketing',
  },
};

const BasketBar = ({
  variant = 'sticky',
  category,
  loadedIds,
  totalCount,
  onShareClick,
}: {
  variant?: BasketBarVariant;
  // The category whose loaded rows "Select all N loaded" acts on.
  // Omit to hide the select-all entirely (no host list in scope).
  category?: SharedBasketItemCategory;
  loadedIds?: number[];
  totalCount?: number;
  onShareClick?: () => void;
}) => {
  const { items, count, addMany, clear, idsFor } = useBasketContext();
  const canShare = useHasUserPermissions(SHARE_BASKET_PERMISSIONS);
  const { download, loading: downloadLoading } = useDownload();
  // The bar spans the middle of the bottom edge, between whatever is docked in
  // the corners: the download manager on the left, the upload manager on the right
  const floating = variant !== 'footer';
  const dockedLeft = useSideClearance('left');
  const dockedRight = useSideClearance('right');
  const leftClearance = floating ? dockedLeft : 0;
  const rightClearance = floating ? dockedRight : 0;
  const [rootElement, setRootElement] =
    useState<HTMLDivElement | null>(null);
  const [overlap, setOverlap] = useState({ left: 0, right: 0 });

  // Only the fixed bar: the others sit in normal flow, or inside the search
  // modal that covers the page reading this.
  useReportBottomOverlay(
    'basketBar',
    'center',
    variant === 'fixed' ? rootElement : null,
  );

  useLayoutEffect(() => {
    const container = rootElement?.parentElement;
    if (variant !== 'sticky' || !container) return;

    // The container's box does not depend on the bar's margins, so measuring
    // it cannot feed back into itself.
    const measure = () => {
      const rect = container.getBoundingClientRect();
      let left = Math.max(0, leftClearance - rect.left);
      let right = Math.max(
        0,
        rect.right - (window.innerWidth - rightClearance),
      );

      // Both corners at once can ask for more than the column has. Give up
      // what is there, proportionally, and keep the rest.
      const allowance = Math.max(0, rect.width - MIN_BAR_WIDTH);
      if (left + right > allowance) {
        const scale = allowance / (left + right);
        left = Math.round(left * scale);
        right = Math.round(right * scale);
      }

      const next = { left, right };

      setOverlap((current) =>
        current.left === next.left && current.right === next.right
          ? current
          : next,
      );
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    // Past its max width the column stops growing but keeps moving, which no
    // observer reports.
    window.addEventListener('resize', measure);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [rootElement, variant, leftClearance, rightClearance]);

  // /api/download takes one category per request, so a basket spanning both
  // sections is fetched as one zip per category.
  const handleDownloadAll = () => {
    (Object.keys(CategoryLabels) as SharedBasketItemCategory[])
      .map((category) => ({ category, fileIds: idsFor(category) }))
      .filter(({ fileIds }) => fileIds.length > 0)
      .forEach(({ category, fileIds }) =>
        download({ category, file_ids: fileIds }),
      );
  };

  if (count === 0) return null;

  const loadedCount = loadedIds?.length ?? 0;
  const selectedIds = new Set(category ? idsFor(category) : []);
  const unselectedLoaded =
    category && loadedIds
      ? loadedIds.filter((id) => !selectedIds.has(id))
      : [];

  // Only worth showing when the basket actually spans both categories -
  // otherwise it just restates the count above it.
  const breakdown = (
    Object.keys(CategoryLabels) as SharedBasketItemCategory[]
  )
    .map((key) => ({
      key,
      count: items.filter((item) => item.category === key).length,
    }))
    .filter((entry) => entry.count > 0);

  return (
    <Root
      ref={setRootElement}
      $variant={variant}
      $leftClearance={leftClearance}
      $rightClearance={rightClearance}
      $overlap={overlap}
      role="region"
      aria-label="Download basket"
    >
      <Summary>
        <Count aria-live="polite">
          <FormattedMessage
            id="filesInBasket"
            defaultMessage="{count, plural, one {# file} other {# files}} in basket"
            values={{ count }}
          />
        </Count>
        <Breakdown>
          {breakdown.length > 1 &&
            breakdown.map((entry, index) => (
              <span key={entry.key}>
                {index > 0 ? ' · ' : null}
                <FormattedMessage
                  id={CategoryLabels[entry.key].id}
                  defaultMessage={
                    CategoryLabels[entry.key].defaultMessage
                  }
                  values={{ count: entry.count }}
                />
              </span>
            ))}
          {totalCount != null && loadedCount > 0 && (
            <Muted>
              {breakdown.length > 1 ? ' · ' : null}
              <FormattedMessage
                id="basketFilesLoaded"
                defaultMessage="{loaded} of {total} files loaded"
                values={{ loaded: loadedCount, total: totalCount }}
              />
            </Muted>
          )}
        </Breakdown>
      </Summary>
      <Actions>
        {SHOW_SELECT_ALL_LOADED &&
          category &&
          unselectedLoaded.length > 0 && (
            <GhostButton
              variant="text"
              onClick={() => addMany(category, unselectedLoaded)}
            >
              <FormattedMessage
                id="selectAllLoaded"
                defaultMessage="Select all {count} loaded"
                values={{ count: loadedCount }}
              />
            </GhostButton>
          )}
        <GhostButton variant="text" onClick={clear}>
          <FormattedMessage
            id="clearBasket"
            defaultMessage="Clear basket"
          />
        </GhostButton>
        <DownloadAllButton
          variant="contained"
          onClick={handleDownloadAll}
          loading={downloadLoading}
          startIcon={<DownloadIcon />}
        >
          <FormattedMessage
            id="downloadBasket"
            defaultMessage="Download all ({count})"
            values={{ count }}
          />
        </DownloadAllButton>
        {canShare && (
          <ShareButton
            variant="contained"
            onClick={onShareClick}
            startIcon={<LinkIcon />}
          >
            <FormattedMessage
              id="shareSelection"
              defaultMessage="Share selection"
            />
          </ShareButton>
        )}
      </Actions>
    </Root>
  );
};

// `sticky`, never `fixed`: a sticky element keeps its slot in normal flow, so
// the last row and the infinite-scroll trigger (which fires ~200px from the
// bottom) can always be scrolled clear of the bar.
const Root = styled('div', {
  shouldForwardProp: (prop) =>
    prop !== '$variant' &&
    prop !== '$leftClearance' &&
    prop !== '$rightClearance' &&
    prop !== '$overlap',
})<{
  $variant: BasketBarVariant;
  $leftClearance: number;
  $rightClearance: number;
  $overlap: { left: number; right: number };
}>(({
  theme,
  $variant,
  $leftClearance,
  $rightClearance,
  $overlap,
}) => {
  const fixedLeft = Math.max(FIXED_GUTTER, $leftClearance);
  const fixedRight = Math.max(FIXED_GUTTER, $rightClearance);

  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.75, 2.5),
    borderRadius: 12,
    background: theme.palette.grey900,
    color: theme.palette.white,
    ...($variant === 'fixed'
      ? {
          // The game page pins the bar instead of sticking it: `Paper`
          // (overflow: hidden) and `Details` (overflow-x: auto, which computes
          // overflow-y to auto) both make themselves scroll containers, so a
          // sticky bar would resolve against a box that never scrolls and just
          // sit at the end of the list. Hosts pair this with
          // FIXED_BASKET_BAR_CLEARANCE of bottom padding.
          position: 'fixed',
          bottom: theme.spacing(2),
          // Pinned to both gutters rather than centred, so a manager docked in
          // one corner only eats into that side; with both offsets set and a
          // max width, the auto margins centre what is left. `min()` keeps
          // MIN_BAR_WIDTH of bar when a viewport cannot grant both corners.
          left: `min(${fixedLeft}px, calc(100vw - ${
            fixedRight + MIN_BAR_WIDTH
          }px))`,
          right: `min(${fixedRight}px, calc(100vw - ${
            fixedLeft + MIN_BAR_WIDTH
          }px))`,
          marginInline: 'auto',
          maxWidth: 1262,
          transition: 'left 0.2s ease-in-out, right 0.2s ease-in-out',
          boxShadow: '0 8px 30px rgba(0, 0, 0, 0.25)',
          zIndex: 1000,
        }
      : {}),
    ...($variant === 'sticky'
      ? {
          position: 'sticky',
          // Floats clear of the scrollport's bottom edge rather than sitting
          // flush on it; the matching marginBottom keeps that gap once the
          // list is scrolled to the end and the bar settles into its slot.
          bottom: theme.spacing(2),
          marginTop: theme.spacing(3.5),
          marginBottom: theme.spacing(2),
          // Only the part that actually reaches a docked widget, so a wide
          // screen keeps the full-width bar it always had.
          marginLeft: `${$overlap.left}px`,
          marginRight: `${$overlap.right}px`,
          transition: 'margin 0.2s ease-in-out',
          boxShadow: '0 8px 30px rgba(0, 0, 0, 0.25)',
          zIndex: 2,
        }
      : {}),
  };
});

const Summary = styled('div')({});

const Count = styled('div')({
  fontWeight: 700,
  fontSize: 14.5,
  lineHeight: 1.35,
});

const Breakdown = styled('div')(({ theme }) => ({
  marginTop: 2,
  fontWeight: 500,
  fontSize: 13,
  lineHeight: 1.35,
  color: theme.palette.grey300,
}));

const Muted = styled('span')(({ theme }) => ({
  color: theme.palette.grey500,
}));

const Actions = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: theme.spacing(1.25),
}));

// Every button in the bar shares one box so the ghost buttons and the Share
// button line up; the transparent border on Share matches the ghost outline's
// 1px, which would otherwise make the outlined buttons 2px taller.
const barButtonStyles = {
  textTransform: 'none',
  fontWeight: 600,
  fontSize: 13.5,
  lineHeight: 1.4,
  height: 38,
  boxSizing: 'border-box',
  padding: '0 16px',
  border: '1px solid transparent',
  whiteSpace: 'nowrap',
  // MUI's own icons render at 20px and a raw imported SVG at its own 24px,
  // either of which would out-grow the text and make that one button taller.
  '& .MuiButton-startIcon > *:first-of-type': {
    fontSize: 18,
  },
  '& .MuiButton-startIcon svg': {
    width: 18,
    height: 18,
  },
} as const;

const ShareButton = styled(Button)(barButtonStyles);

// Sits between the ghost buttons and the ruby Share button: solid enough to
// read as a real action, quiet enough not to compete with Share.
const DownloadAllButton = styled(Button)(({ theme }) => ({
  ...barButtonStyles,
  background: theme.palette.white,
  color: theme.palette.grey900,
  // download.svg ships a hardcoded white stroke, which would vanish on a
  // light button.
  '& svg path': {
    stroke: theme.palette.grey900,
  },
  '&:hover': {
    background: theme.palette.grey200,
  },
}));

const GhostButton = styled(Button)(({ theme }) => ({
  ...barButtonStyles,
  borderColor: theme.palette.grey600,
  color: theme.palette.grey200,
  '&:hover': {
    borderColor: theme.palette.grey400,
    color: theme.palette.white,
    background: 'transparent',
  },
}));

export default BasketBar;
